/* eslint-disable @typescript-eslint/naming-convention */
import { Auth0Client } from "@auth0/auth0-spa-js";
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { tokenManager } from "../../auth/tokenManager";
import { APIError } from "../../errors/apiError";
import { logger } from "../../logger";
import { createAxiosClient } from "./axiosApiClient";

/**
 * Base abstract API client class that provides common functionality for all API clients.
 *
 * This class standardizes:
 * - Authentication
 * - Error handling
 * - Request execution
 * - Response parsing
 */
export abstract class BaseApiClient {
  /** Default timeout for API requests in milliseconds */
  protected static readonly API_TIMEOUT = 60000; // 60 seconds

  /** Base URL for API requests */
  protected readonly baseUrl: string;

  /** Auth0 client for authentication */
  protected readonly auth0Client?: Auth0Client;

  /** Function to get authentication token */
  protected readonly getTokenFn: () => Promise<string>;

  /**
   * Creates a new instance of the BaseApiClient
   *
   * @param baseUrl - Base URL for API requests
   * @param getTokenOrAuth0Client - Either a function to get a token or an Auth0Client instance
   * @param auth0Client - Optional Auth0Client instance if getTokenOrAuth0Client is a function
   */
  constructor(
    baseUrl: string,
    getTokenOrAuth0Client: (() => Promise<string>) | Auth0Client,
    auth0Client?: Auth0Client
  ) {
    this.baseUrl = baseUrl;

    if (typeof getTokenOrAuth0Client === "function") {
      this.getTokenFn = getTokenOrAuth0Client;
      this.auth0Client = auth0Client;
    } else {
      this.auth0Client = getTokenOrAuth0Client;
      this.getTokenFn = async () => {
        // Initialize tokenManager with the Auth0 client if not already
        if (!tokenManager.isInitialized() && this.auth0Client) {
          tokenManager.initialize(this.auth0Client);
        }
        return tokenManager.getToken();
      };
    }
  }

  /**
   * Get authentication headers for API requests
   *
   * @returns Promise that resolves to headers object with authentication
   */
  protected async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const token = await this.getTokenFn();
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    } catch (error) {
      throw new APIError(401, "Failed to get authentication token", "AUTH_ERROR");
    }
  }

  /**
   * Execute a GET request
   *
   * @param path - API endpoint path
   * @param params - Optional query parameters
   * @returns Promise that resolves to the response data
   */
  protected async get<T>(path: string, params?: Record<string, any>): Promise<T> {
    const url = this.buildUrl(path, params);
    return this.executeRequest<T>(url, { method: "GET" });
  }

  /**
   * Execute a POST request
   *
   * @param path - API endpoint path
   * @param data - Optional request body
   * @param params - Optional query parameters
   * @returns Promise that resolves to the response data
   */
  protected async post<T>(path: string, data?: any, params?: Record<string, any>): Promise<T> {
    const url = this.buildUrl(path, params);
    return this.executeRequest<T>(url, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Execute a PUT request
   *
   * @param path - API endpoint path
   * @param data - Optional request body
   * @param params - Optional query parameters
   * @returns Promise that resolves to the response data
   */
  protected async put<T>(path: string, data?: any, params?: Record<string, any>): Promise<T> {
    const url = this.buildUrl(path, params);
    return this.executeRequest<T>(url, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Execute a PATCH request
   *
   * @param path - API endpoint path
   * @param data - Optional request body
   * @param params - Optional query parameters
   * @returns Promise that resolves to the response data
   */
  protected async patch<T>(path: string, data?: any, params?: Record<string, any>): Promise<T> {
    const url = this.buildUrl(path, params);
    return this.executeRequest<T>(url, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Execute a DELETE request
   *
   * @param path - API endpoint path
   * @param data - Optional request body
   * @param params - Optional query parameters
   * @returns Promise that resolves to the response data
   */
  protected async delete<T>(path: string, data?: any, params?: Record<string, any>): Promise<T> {
    const url = this.buildUrl(path, params);
    return this.executeRequest<T>(url, {
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Build a URL with query parameters
   *
   * @param path - API endpoint path
   * @param params - Optional query parameters
   * @returns Full URL with query parameters
   */
  protected buildUrl(path: string, params?: Record<string, any>): string {
    const url = new URL(path.startsWith("/") ? `${this.baseUrl}${path}` : path);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            url.searchParams.append(key, value.join(","));
          } else if (typeof value === "object") {
            url.searchParams.append(key, JSON.stringify(value));
          } else {
            url.searchParams.append(key, String(value));
          }
        }
      });
    }

    return url.toString();
  }

  /**
   * Execute an HTTP request
   *
   * @param input - Request URL or Request object
   * @param init - Request initialization options
   * @returns Promise that resolves to the response data
   */
  protected abstract executeRequest<T>(input: RequestInfo, init?: RequestInit): Promise<T>;

  /**
   * Creates an Axios instance with default configuration
   */
  protected createAxiosInstance(): AxiosInstance {
    const instance = createAxiosClient(this.baseUrl, BaseApiClient.API_TIMEOUT);

    // Add request interceptor for authentication
    instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          // Only set Bearer token if no Authorization header is already present
          if (!config.headers.Authorization) {
            const token = await this.getTokenFn();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          }
          return config;
        } catch (error) {
          if ((error as any).code === "NOT_INITIALIZED") {
            console.warn("TokenManager not initialized, waiting for auth bootstrap");
            await tokenManager.waitUntilInitialized();
            const token = await this.getTokenFn();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
          }
          throw error;
        }
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const data = error.response?.data as any;
        const msg = data?.error ?? data?.message ?? error.message;
        console.error(`[API] ${error.config?.method?.toUpperCase()} ${error.config?.url} → ${msg}`);

        if (error.response) {
          throw new APIError(
            error.response.status,
            msg,
            (error.response.data as any)?.code,
            error.response.data as any
          );
        }
        throw error;
      }
    );

    return instance;
  }
}

/**
 * API client implementation that uses the Fetch API for HTTP requests
 */
export class FetchApiClient extends BaseApiClient {
  /**
   * Execute an HTTP request using the Fetch API
   *
   * @param input - Request URL or Request object
   * @param init - Request initialization options
   * @returns Promise that resolves to the response data
   */
  protected async executeRequest<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), BaseApiClient.API_TIMEOUT);

    try {
      const headers = await this.getAuthHeaders();
      const requestInit: RequestInit = {
        ...init,
        headers: {
          ...headers,
          ...(init?.headers || {}),
        },
        signal: controller.signal,
      };

      logger.debug(`API Request: ${typeof input === "string" ? input : "Request object"}`, {
        method: init?.method,
        url: typeof input === "string" ? input : "Request object",
      });

      const response = await fetch(input, requestInit);

      logger.debug(`API Response: ${response.status} ${response.statusText}`, {
        method: init?.method,
        url: typeof input === "string" ? input : "Request object",
      });

      // Handle common error responses
      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      // Handle no-content responses
      if (response.status === 204) {
        return {} as T;
      }

      try {
        return await response.json();
      } catch (error) {
        throw new APIError(
          500,
          `Invalid response format: ${error instanceof Error ? error.message : "Could not parse JSON"}`,
          "PARSE_ERROR"
        );
      }
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      const errorObj = error as Error;

      if (errorObj.name === "AbortError") {
        throw new APIError(504, "Request timed out", "TIMEOUT");
      }

      throw new APIError(
        500,
        `Network request failed: ${errorObj.message || "Unknown error"}`,
        "NETWORK_ERROR"
      );
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Handle error responses from the API
   *
   * @param response - Fetch API Response object
   */
  private async handleErrorResponse(response: Response): Promise<never> {
    let errorData: any = {};

    try {
      errorData = await response.json();
    } catch {
      // If we can't parse the error response as JSON, use an empty object
    }

    const errorMessage = errorData?.error || errorData?.message || response.statusText;
    const errorCode = errorData?.code || `HTTP_${response.status}`;

    // Handle specific error cases
    if (response.status === 401) {
      if (this.auth0Client) {
        await this.auth0Client.logout();
      }
      throw new APIError(401, "Session expired - please login again", "SESSION_EXPIRED");
    }

    if (response.status === 429) {
      const retryAfter = response.headers.get("Retry-After") || "1";
      throw new APIError(
        429,
        `Rate limit exceeded. Retry after ${retryAfter} seconds.`,
        "RATE_LIMIT_EXCEEDED",
        { retryAfter }
      );
    }

    throw new APIError(response.status, errorMessage, errorCode, errorData);
  }
}

/**
 * API client implementation that uses Axios for HTTP requests
 */
export class AxiosApiClient extends BaseApiClient {
  client: AxiosInstance;

  constructor(
    baseUrl: string,
    getTokenOrAuth0Client: (() => Promise<string>) | Auth0Client,
    auth0Client?: Auth0Client
  ) {
    super(baseUrl, getTokenOrAuth0Client, auth0Client);
    this.client = this.createAxiosInstance();
  }

  /**
   * Execute an HTTP request using Axios
   *
   * @param input - Request URL or Request object
   * @param init - Request initialization options
   * @returns Promise that resolves to the response data
   */
  protected async executeRequest<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
    // Guarantee client instance before every request (covers edge cases where
    // the constructor safeguard ran in a different bundle copy)
    // this.ensureClient(); // No longer needed

    const url = typeof input === "string" ? input : input.url;
    const method = init?.method?.toLowerCase() || "get";
    const data = init?.body
      ? typeof init.body === "string"
        ? JSON.parse(init.body)
        : init.body
      : undefined;

    try {
      logger.debug(`API Request: ${method.toUpperCase()} ${url} %j`, {
        baseUrl: this.baseUrl,
        data,
      });

      // Create a proper Axios request config
      const config = {
        url,
        method,
        data,
        baseURL: this.baseUrl,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      // Interceptor will add auth header
      const response = await this.client.request<T>(config);

      logger.debug(`API Response: ${response.status} ${method.toUpperCase()} ${url}`);
      return response.data;
    } catch (error) {
      // Delegate error handling
      this.handleError(error, method, url);
    }
  }

  static resetInstance(className?: string) {
    // No-op: singleton logic removed
  }

  // Extracted error handling logic
  private handleError(error: any, method: string, url: string): never {
    logger.error(`API Error: ${method.toUpperCase()} ${url}`, error);

    if (axios.isCancel(error)) {
      throw new APIError(0, error.message || "Request canceled", "REQUEST_CANCELED");
    }

    if (error?.response) {
      const status = error.response.status;
      const errorData = error.response.data || {};
      const errorMessage = errorData.error || errorData.message || error.message;
      const errorCode = errorData.code || `HTTP_${status}`;

      if (status === 401) {
        logger.warn("API returned 401, potentially logging out.");
        this.auth0Client
          ?.logout({
            logoutParams: { returnTo: window.location.origin },
          })
          .catch((logoutError) => logger.error("Error during logout attempt:", logoutError));
        throw new APIError(401, "Session expired - please login again", "SESSION_EXPIRED");
      }

      throw new APIError(status, errorMessage, errorCode, errorData);
    }

    if (error?.request) {
      throw new APIError(0, "No response received from server", "NETWORK_ERROR");
    }

    // Handle case where error.config is undefined
    if (!error?.config) {
      throw new APIError(0, "Request configuration error", "REQUEST_CONFIG_ERROR");
    }

    // Default/fallback error
    throw new APIError(0, error?.message || "Request failed", "REQUEST_SETUP_ERROR");
  }
}
